import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  UserCheck,
  UserX,
  Briefcase,
  Mail,
  Phone,
  Shield,
} from "lucide-react";
import { Employee } from "../../types/employee";
import api from "../../services/api";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

const COLORS = [
  "#885ABB",
  "#A855F7",
  "#C084FC",
  "#E9D5FF",
  "#F3E8FF",
  "#6B21A8",
];

export default function EmployeesSummaryPage() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get(`/admin/employees`);
      const data = response.data;
      let employeeList: Employee[] = [];

      if (data.employees) {
        employeeList = data.employees;
      } else if (data.success && data.data) {
        if (data.data.data) {
          employeeList = data.data.data;
        } else {
          employeeList = data.data;
        }
      }
      setEmployees(employeeList || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const active = employees.filter(
      (e) => e.status === "active" || !e.status
    ).length;
    const inactive = employees.filter((e) => e.status === "inactive").length;
    const uniqueRoles = Array.from(
      new Set(employees.map((e) => e.role).filter(Boolean))
    );
    return { active, inactive, rolesCount: uniqueRoles.length, uniqueRoles };
  }, [employees]);

  const roleData = useMemo(() => {
    const counts: Record<string, number> = {};
    employees.forEach((e) => {
      if (e.role) {
        counts[e.role] = (counts[e.role] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .map(([name, value], index) => ({
        name,
        value,
        color: COLORS[index % COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [employees]);

  const statusData = useMemo(
    () => [
      { name: "Active", value: stats.active, color: "#885ABB" },
      { name: "Inactive", value: stats.inactive, color: "#E9D5FF" },
    ],
    [stats]
  );

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <GlobalLoader description="Loading employee summary..." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Employees Summary"
        description="Detailed analytics and overview of your staff members"
        icon={Users}
        variant="default"
        className="mb-0"
      />

      <div className="space-y-6 pt-4">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Staff"
            value={employees.length}
            icon={Users}
            trend="+1 this month"
            trendUp={true}
          />
          <MetricCard
            label="Active Employees"
            value={stats.active}
            icon={UserCheck}
            subValue={`${Math.round(
              (stats.active / (employees.length || 1)) * 100
            )}% of total`}
          />
          <MetricCard
            label="Inactive Employees"
            value={stats.inactive}
            icon={UserX}
          />
          <MetricCard
            label="Unique Roles"
            value={stats.rolesCount}
            icon={Briefcase}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Role Distribution Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-6">
              Staff Role Distribution
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={roleData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#885ABB"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  >
                    {roleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Pie Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-4">
              Staff Status
            </h3>
            <div className="h-[200px] w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={0}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-gray-900">
                  {stats.active}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  Active
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4">
              {statusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {item.name} ({item.value})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Employees Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#402B69]">
              Team Members Preview
            </h3>
            <Link
              to="/manage/employee"
              className="text-sm text-[#885ABB] font-medium hover:text-[#6f47a2] transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#885ABB] text-white font-medium">
                <tr>
                  <th className="px-6 py-3 font-semibold">Employee</th>
                  <th className="px-6 py-3 font-semibold text-center">Role</th>
                  <th className="px-6 py-3 font-semibold text-right">
                    Contact
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.slice(0, 6).map((emp) => (
                  <tr
                    key={emp.id}
                    className="group hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-purple-50 flex items-center justify-center text-[#885ABB] font-bold text-sm overflow-hidden">
                          {emp.image ? (
                            <img
                              src={emp.image}
                              alt={emp.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            emp.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 group-hover:text-[#885ABB] transition-colors">
                            {emp.name}
                          </p>
                          <p className="text-xs text-gray-500">{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-[#885ABB] border border-purple-100">
                        {emp.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex flex-col items-end gap-1">
                        {emp.mobile ? (
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <Phone className="w-3.5 h-3.5" />
                            <span className="text-xs">{emp.mobile}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">
                            No mobile
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  subValue,
  trend,
  trendUp,
}: any) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-[#885ABB]" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${trendUp ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{label}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && (
            <span className="text-xs text-gray-400 truncate max-w-[120px]">
              {subValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
